import { api } from "../api";
import {
  PromptsResponse,
  ValidateResponse,
  IngestJobResponse,
  IngestionJob,
  IngestionFlags,
  OnExistingAlternatives,
  ValidateOptions,
} from "./content-ingestion.types";

export const getPrompts = async (): Promise<PromptsResponse> => {
  const response = await api.get(`/content-ingestion/prompts`);
  return response.data;
};

/**
 * `options.replace` (SCRUM-1532) validates the doc as a replacement of the
 * course's content with the same IDs: the API leaves out the checks that only
 * hold when lessons are added. Sent only when set.
 */
export const validateDocument = async (
  documentId: string,
  options?: ValidateOptions,
): Promise<ValidateResponse> => {
  const query = options?.replace ? "?replace=true" : "";
  const response = await api.get(
    `/content-ingestion/validate/${encodeURIComponent(documentId)}${query}`,
  );
  return response.data;
};

/**
 * Starts ingestion in background and returns the jobId immediately.
 * `append` carries the admin's explicit confirmation that the parsed lessons
 * must be added to the ones the course/project already has; without it the API
 * rejects a non-empty target with a 409.
 * `onExistingAlternatives` carries the admin's global decision after an
 * ACTIVITIES_HAVE_ALTERNATIVES 409 on an alternatives-template doc; it is
 * omitted from the body until that decision exists.
 * `flags.replace` (SCRUM-1532) replaces the course's content in place, keeping
 * its IDs, and `flags.force` uploads over edits made in the platform; each is
 * sent only when set.
 */
export const startIngestion = async (
  documentId: string,
  append = false,
  onExistingAlternatives?: OnExistingAlternatives,
  flags: IngestionFlags = {},
): Promise<IngestJobResponse> => {
  const response = await api.post(`/content-ingestion`, {
    document_id: documentId,
    append,
    ...(onExistingAlternatives !== undefined && {
      on_existing_alternatives: onExistingAlternatives,
    }),
    ...(flags.replace && { replace: true }),
    ...(flags.force && { force: true }),
  });
  return response.data;
};

/** Polls the ingestion job status by ID. */
export const getIngestionJob = async (jobId: number): Promise<IngestionJob> => {
  const response = await api.get(`/content-ingestion/jobs/${jobId}`);
  return response.data;
};
